import {
  ref,
  onMounted,
  defineComponent,
  reactive,
  computed,
} from "@vue/composition-api";
import { v4 as uuidv4 } from "uuid";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import BuildWorkOrderTaskEditForm from "@/components/WorkOrders/BuildWorkOrderTaskEditForm.vue";
import BuildWorkOrderTaskLineItemEditForm from "@/components/WorkOrders/BuildWorkOrderTaskLineItemEditForm.vue";

import { newSortOrderValue } from "@/utilities/sortOrder";

import {
  CWorkOrderTask,
  CWorkOrderTaskLineItem,
} from "@/store/modules/workOrderTasks";

import { LineItem } from "@/store/modules/lineItems";

import { JSConn } from "@/utilities/js-conn";

import { WorkOrderDetailed } from "@/store/modules/workOrders";

export default defineComponent({
  name: "buildWorkOrder",
  components: {
    LoadingAnimation,
    BuildWorkOrderTaskEditForm,
    BuildWorkOrderTaskLineItemEditForm,
  },

  props: {
    workOrderId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const secondDataLoaded = ref(false);
    const loadingMessage = ref("Preparing the form...");

    const editingTaskId = ref("");
    const selectedTaskId = ref("");

    const editingTaskLineItemId = ref("");

    const workOrder = computed(() => {
      return $store.getters.workOrder as WorkOrderDetailed;
    });

    const newTask = reactive<CWorkOrderTask>({
      id: "new",
      name: "",
      description: "",
      start_date: "",
      completed_date: "",
      status: "pending",
      sort_order: 200,
      user_id: "",
      work_order_id: props.workOrderId,
      work_order_task_line_items: [],
    });

    const workOrderTasks = computed(() => {
      return $store.getters.workOrderTasks as CWorkOrderTask[];
    });

    const lineItems = computed(() => {
      return $store.getters.lineItems;
    });

    const editTask = (task: CWorkOrderTask) => {
      if (editingTaskId.value === task.id) {
        editingTaskId.value = "";
      } else {
        editingTaskId.value = task.id;
      }
    };

    const editTaskSuccessful = (
      _task: CWorkOrderTask | CWorkOrderTaskLineItem,
    ) => {
      editingTaskId.value = "";
      editingTaskLineItemId.value = "";
      newTask.id = "new";
      newTask.name = "";
      newTask.description = "";
      reloadWorkOrder();
    };

    const editTaskLineItem = (lnItem: CWorkOrderTaskLineItem) => {
      if (editingTaskLineItemId.value === lnItem.id) {
        editingTaskLineItemId.value = "";
      } else {
        editingTaskLineItemId.value = lnItem.id;
      }
    };

    const selectTask = (task: CWorkOrderTask) => {
      if (selectedTaskId.value === task.id) {
        selectedTaskId.value = "";
      } else {
        selectedTaskId.value = task.id;
      }
    };

    const moveTask = (direction: "up" | "down", task: CWorkOrderTask) => {
      const newSortOrder = newSortOrderValue(task, direction, [
        ...workOrderTasks.value,
      ]);
      if (newSortOrder.noChangeNeeded) {
        return;
      } else {
        const jsConn = new JSConn();
        jsConn.formParams = {
          id: task.id,
          sort_order: newSortOrder.newSortOrder,
        };

        $store.dispatch("updateWorkOrderTask", jsConn).then(() => {
          // ctx.emit("tasksChanged");
          reloadWorkOrder();
        });
      }
    };

    const deleteTask = (task: CWorkOrderTask) => {
      const jsConn = new JSConn();
      jsConn.formParams = task;
      $store.dispatch("deleteWorkOrderTask", jsConn).then(() => {
        reloadWorkOrder();
      });
    };

    const copyTask = (task: CWorkOrderTask) => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        work_order_task_id: task.id,
      };

      $store
        .dispatch("createWorkOrderTaskFromWorkOrderTask", jsConn)
        .then(() => {
          reloadWorkOrder();
        });
    };

    const deleteLineItem = (lineItem: CWorkOrderTaskLineItem) => {
      const jsConn = new JSConn();
      jsConn.formParams = lineItem;
      $store.dispatch("deleteWorkOrderTaskLineItem", jsConn).then(() => {
        reloadWorkOrder();
      });
    };

    const reloadWorkOrder = () => {
      let dataPromises = [];
      const jsConn = new JSConn();

      jsConn.formParams = {
        id: props.workOrderId,
      };

      dataPromises.push($store.dispatch("needWorkOrder", jsConn));

      jsConn.formParams = {
        id: props.workOrderId,
      };

      Promise.all(dataPromises).finally(() => {
        dataLoaded.value = true;
      });
    };

    onMounted(() => {
      reloadWorkOrder();

      let secondPanePromises = [];
      const jsConnLineItems = new JSConn();

      secondPanePromises.push(
        $store.dispatch("needLineItems", jsConnLineItems),
      );

      Promise.all(secondPanePromises).finally(() => {
        secondDataLoaded.value = true;
      });
    });

    const selectedRowClassObject = (task: CWorkOrderTask) => {
      let isRowSelected = false;
      if (task.id === selectedTaskId.value) {
        isRowSelected = true;
      } else {
        isRowSelected = false;
      }

      return {
        "hb-selected-row": isRowSelected,
      };
    };

    const addToSelectedTask = (lnItem: LineItem) => {
      if (selectedTaskId.value !== "") {
        const jsConn = new JSConn();
        jsConn.formParams = {
          id: uuidv4(),
          item_date: null,
          quantity: 0,
          rate: lnItem.default_rate,
          sort_order: 200,
          line_item_id: lnItem.id,
          work_order_task_id: selectedTaskId.value,
        };

        $store.dispatch("createWorkOrderTaskLineItem", jsConn).then(() => {
          reloadWorkOrder();
        });
      }
    };

    return {
      dataLoaded,
      secondDataLoaded,
      loadingMessage,
      newTask,

      editTaskSuccessful,

      moveTask,
      deleteTask,
      copyTask,

      deleteLineItem,

      workOrder,
      workOrderTasks,

      lineItems,

      editTask,
      editingTaskId,

      selectTask,
      selectedTaskId,
      selectedRowClassObject,

      addToSelectedTask,

      editingTaskLineItemId,
      editTaskLineItem,
    };
  },
});
